import { useEffect, useState } from "react";
import {
  HelpCircle,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Undo2,
} from "lucide-react";
import type { EventEntry } from "../../../src/fields.ts";
import { type Category, cats } from "../../../src/category.ts";
import { api } from "../api.ts";
import { db } from "../db.ts";
import { id } from "@instantdb/react";
import { Spinner } from "./Spinner.tsx";
import { EventCard } from "./EventCard.tsx";
import { Input, Label, Select } from "./ui/index.ts";

const toQueryDatetime = (date: Date) => date.toISOString();

const toQueryEndOfDay = (date: Date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  ).toISOString();

const createDefaultQuery = (
  selectedCategory: Category | "",
  selectedLocation: string,
) => {
  const now = new Date();
  const end = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 30,
    now.getHours(),
    now.getMinutes(),
    0,
    0,
  );

  return {
    time: {
      startDatetime: toQueryDatetime(now),
      endDatetime: toQueryEndOfDay(end),
    },
    onlyStartedInRange: true,
    limit: 500,
    ...(selectedCategory ? { categories: [selectedCategory] } : {}),
    ...(selectedLocation.trim() ? { location: selectedLocation.trim() } : {}),
  };
};

const withoutSwipedEvents = (
  events: EventEntry[],
  swipedEventIds: string[],
) => {
  const swiped = new Set(swipedEventIds);
  return events.filter(({ id }) => !swiped.has(id));
};

const recordSwipe = (
  userId: string,
  event: EventEntry,
  direction: "like" | "dislike",
  swipeId: string,
) =>
  db.transact(
    db.tx.swipes[swipeId].create({
      eventId: event.id,
      direction,
      timestamp: Date.now(),
    }).link({ user: userId }),
  );

interface SwipeButtonsProps {
  loading: boolean;
  currentIndex: number;
  totalEvents: number;
  onSwipe: (direction: "like" | "dislike") => void;
  onSkip: () => void;
  onUndo: () => void;
  canUndo: boolean;
}

const SwipeButtons = (
  {
    loading,
    currentIndex,
    totalEvents,
    onSwipe,
    onSkip,
    onUndo,
    canUndo,
  }: SwipeButtonsProps,
) => (
  <div className="space-y-3">
    {/* Primary Actions */}
    <div className="flex gap-3">
      <button
        type="button"
        onClick={() => onSwipe("dislike")}
        disabled={loading}
        className="flex-1 bg-gradient-to-r from-rose-500/25 to-red-600/25 hover:from-rose-500/40 hover:to-red-600/40 border border-rose-500/40 hover:border-rose-500/60 text-rose-200 font-bold py-3.5 px-4 rounded-2xl transition transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-rose-500/10 text-sm sm:text-base"
      >
        <ThumbsDown className="w-5 h-5" /> Pass
      </button>
      <button
        type="button"
        onClick={() => onSwipe("like")}
        disabled={loading}
        className="flex-1 bg-gradient-to-r from-emerald-500/25 to-green-600/25 hover:from-emerald-500/40 hover:to-green-600/40 border border-emerald-500/40 hover:border-emerald-500/60 text-emerald-200 font-bold py-3.5 px-4 rounded-2xl transition transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/10 text-sm sm:text-base"
      >
        <ThumbsUp className="w-5 h-5" /> Like & Save
      </button>
    </div>

    {/* Secondary Actions */}
    <div className="flex gap-3">
      <button
        type="button"
        onClick={onUndo}
        disabled={loading || !canUndo}
        className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-xl transition transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-30 flex items-center justify-center gap-1.5 text-xs font-semibold"
        title="Undo last action"
      >
        <Undo2 className="w-4 h-4" /> Undo
      </button>
      <button
        type="button"
        onClick={onSkip}
        disabled={loading}
        className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-xl transition transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-1.5 text-xs font-semibold"
      >
        <HelpCircle className="w-4 h-4" /> Not sure
      </button>
    </div>

    <p className="text-center text-xs text-gray-400 pt-1 font-medium">
      Event {currentIndex + 1} of {totalEvents}
    </p>
  </div>
);

export const EventSwiper = () => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [history, setHistory] = useState<{ swipeId?: string; index: number }[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [fetchingEvents, setFetchingEvents] = useState(true);
  const [category, setCategory] = useState<Category | "">("");
  const [locationInput, setLocationInput] = useState(() => {
    if (typeof globalThis.localStorage !== "undefined") {
      return globalThis.localStorage.getItem("taste_location") || "";
    }
    return "";
  });
  const [debouncedLocation, setDebouncedLocation] = useState(locationInput);
  const [isPaired, setIsPaired] = useState<boolean | null>(null);
  const [checkingPairing, setCheckingPairing] = useState(true);
  const user = db.useAuth().user;

  useEffect(() => {
    if (!user?.email) {
      setIsPaired(false);
      setCheckingPairing(false);
      return;
    }
    setCheckingPairing(true);
    api({
      endpoint: "checkUserPairing",
      payload: { email: user.email },
    })
      .then((res) => {
        setIsPaired(res.isPaired);
      })
      .catch((err) => {
        console.error("Failed to check pairing status:", err);
        setIsPaired(false);
      })
      .finally(() => {
        setCheckingPairing(false);
      });
  }, [user?.email]);

  const swipesQuery = db.useQuery(
    user
      ? {
        $users: {
          $: {
            where: { id: user.id },
          },
          swipes: {},
        },
      }
      : null,
  );

  const currentUser = swipesQuery.data?.$users?.[0];
  const isPhoneVerified = Boolean(
    currentUser?.phoneVerified ||
      (currentUser?.phone && currentUser.phone.length >= 7),
  );
  const effectiveIsPaired = Boolean(isPhoneVerified || isPaired);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLocation(locationInput);
    }, 600);
    return () => clearTimeout(timer);
  }, [locationInput]);

  useEffect(() => {
    if (typeof globalThis.localStorage !== "undefined") {
      globalThis.localStorage.setItem("taste_location", locationInput);
    }
  }, [locationInput]);

  useEffect(() => {
    setHistory([]);
    if (debouncedLocation.trim() === "") {
      setEvents([]);
      setFetchingEvents(false);
      return;
    }
    if (user && swipesQuery.isLoading) return;
    setFetchingEvents(true);
    setCurrentIndex(0);
    const swipedEventIds = swipesQuery.data?.$users[0]?.swipes.map(
      ({ eventId }) => eventId,
    ) ?? [];
    api({
      endpoint: "getEvents",
      payload: {
        ...createDefaultQuery(category, debouncedLocation),
        userId: user?.id,
      },
    })
      .then((events) => setEvents(withoutSwipedEvents(events, swipedEventIds)))
      .catch(console.error)
      .finally(() => setFetchingEvents(false));
  }, [user?.id, swipesQuery.isLoading, category, debouncedLocation]);

  const handleSwipe = async (direction: "like" | "dislike") => {
    if (loading) return;
    const currentEvent = events[currentIndex];
    if (!currentEvent) return;

    const swipeId = id();
    setHistory((prev) => [...prev, { swipeId, index: currentIndex }]);
    setLoading(true);
    if (user) {
      try {
        await recordSwipe(user.id, currentEvent, direction, swipeId);
      } catch (err) {
        console.error("Failed to record swipe", err);
      }
    }
    setLoading(false);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleSkip = () => {
    if (loading) return;
    if (currentIndex >= events.length) return;
    setHistory((prev) => [...prev, { index: currentIndex }]);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleUndo = async () => {
    if (loading || history.length === 0) return;
    const lastAction = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setCurrentIndex(lastAction.index);

    if (lastAction.swipeId && user) {
      setLoading(true);
      try {
        await db.transact(db.tx.swipes[lastAction.swipeId].delete());
      } catch (err) {
        console.error("Failed to delete swipe", err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col items-center py-6 px-4 max-w-2xl mx-auto">
      {/* Filter Controls (always visible) */}
      <div className="w-full mb-6 flex gap-3">
        {/* Category Filter */}
        <div className="flex-1 flex flex-col gap-1.5">
          <Label htmlFor="category-filter">
            Category
          </Label>
          <Select
            id="category-filter"
            value={category}
            onChange={(e) =>
              setCategory(e.currentTarget.value as Category | "")}
            className="w-full"
            wrapperClassName="w-full"
          >
            <option value="">All Categories</option>
            {Object.entries(cats).map(([id, cat]) => (
              <option key={id} value={id}>
                {cat.emoji} {cat.display.en}
              </option>
            ))}
          </Select>
        </div>

        {/* Location Filter */}
        <div className="flex-1 flex flex-col gap-1.5">
          <Label htmlFor="location-filter">
            Location
          </Label>
          <Input
            id="location-filter"
            type="text"
            placeholder="e.g. Tel Aviv"
            value={locationInput}
            onChange={(e) => setLocationInput(e.currentTarget.value)}
          />
        </div>
      </div>

      {/* Main Content Area (Dynamic) */}
      {(checkingPairing && !isPhoneVerified)
        ? (
          <div className="w-full min-h-[480px] flex items-center justify-center">
            <Spinner className="h-10 w-10 text-purple-500" />
          </div>
        )
        : !effectiveIsPaired
        ? (
          <div className="w-full min-h-[480px] bg-white/5 border border-white/10 rounded-3xl shadow-2xl flex flex-col items-center justify-center p-8 text-center animate-fade-up">
            <Sparkles className="w-14 h-14 text-purple-400 mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-white mb-2">
              Pair your email first
            </h2>
            <p className="text-gray-400 text-sm mb-6 max-w-sm">
              Please message <strong>Agent FOMO on WhatsApp</strong>{" "}
              to pair your email and start training your taste!
            </p>
            <p className="text-purple-300 font-mono font-semibold text-sm mb-6 bg-purple-500/10 border border-purple-500/20 px-4 py-2.5 rounded-xl select-all">
              My email is {user?.email}
            </p>
            <a
              href={`https://wa.me/972504244206?text=My%20email%20is%20${
                encodeURIComponent(user?.email || "")
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-full shadow-lg shadow-purple-500/25 transition transform hover:scale-105 active:scale-95"
            >
              Pair on WhatsApp
            </a>
          </div>
        )
        : locationInput.trim() === ""
        ? (
          <div className="w-full min-h-[480px] bg-white/5 border border-white/10 rounded-3xl shadow-2xl flex flex-col items-center justify-center p-8 text-center">
            <h2 className="text-xl font-bold text-white mb-2">
              Enter a location
            </h2>
            <p className="text-gray-400 text-sm">
              Please provide a location above to see events in your area
            </p>
          </div>
        )
        : fetchingEvents
        ? (
          <div className="w-full min-h-[480px] flex items-center justify-center">
            <Spinner className="h-10 w-10 text-purple-500" />
          </div>
        )
        : currentIndex >= events.length
        ? (
          <div className="w-full min-h-[480px] bg-white/5 border border-white/10 rounded-3xl shadow-2xl flex flex-col items-center justify-center p-8 text-center animate-fade-up">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-white mb-2">
              You've reached the end!
            </h2>
            <p className="text-gray-400 text-sm mb-6 max-w-sm">
              No more events to review in this area. Check back later for fresh
              recommendations or adjust your filters.
            </p>
            {history.length > 0 && (
              <button
                type="button"
                onClick={handleUndo}
                disabled={loading}
                className="px-5 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-slate-200 font-semibold transition transform hover:scale-105 active:scale-95 disabled:opacity-50 flex items-center gap-2 text-sm"
              >
                <Undo2 size={16} /> Undo last swipe
              </button>
            )}
          </div>
        )
        : (
          <div className="w-full space-y-4 animate-fade-up">
            <EventCard
              event={events[currentIndex]}
              language="en"
              showTasteControls={false}
              maxPosterHeight="max-h-[360px] sm:max-h-[460px]"
            />
            <div className="p-4 sm:p-5 rounded-3xl bg-white/[0.03] border border-white/10 backdrop-blur-md shadow-2xl">
              <SwipeButtons
                loading={loading}
                currentIndex={currentIndex}
                totalEvents={events.length}
                onSwipe={handleSwipe}
                onSkip={handleSkip}
                onUndo={handleUndo}
                canUndo={history.length > 0}
              />
            </div>
          </div>
        )}
    </div>
  );
};
